import { Helmet } from 'react-helmet-async'
import Card from '@/components/Cards/Card'
import { 
  MapPinIcon,
  CurrencyDollarIcon,
  ClockIcon,
  BriefcaseIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline'

const CareersPage = () => {
  const jobOpenings = [
    {
      id: 'senior-business-consultant-asia-pacific',
      title: 'Senior Business Consultant - Asia Pacific',
      department: 'Consulting Services',
      location: 'Singapore',
      type: 'Full-time',
      salary: '$85,000 - $120,000',
      experience: '5+ years',
      description: 'Lead strategic consulting projects for international market entry in the Asia-Pacific region. Works closely with our Singapore logistics partner, Silk Road Logistics.',
      postedDate: '2024-03-10',
      urgent: true,
      hiringManager: 'Robert Martinez, International Markets Director'
    },
    {
      id: 'financial-analyst-international-markets',
      title: 'Financial Analyst - International Markets',
      department: 'Finance & Administration',
      location: 'Valdoria City HQ',
      type: 'Full-time',
      salary: '$65,000 - $85,000',
      experience: '3+ years',
      description: 'Analyze financial data and market trends to support international investment decisions.',
      postedDate: '2024-03-08',
      urgent: false,
      hiringManager: 'Lisa Wang, Senior Financial Analyst'
    }
  ]

  return (
    <>
      <Helmet>
        <title>Careers - Nexus Global Solutions</title>
        <meta name="description" content="Join our team of international business experts at Nexus Global Solutions. Explore career opportunities in consulting, business development, and global markets." />
        <meta name="keywords" content="nexus global careers, international business jobs, consulting careers, global opportunities" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-800 to-accent-800 text-white py-20">
        <div className="corporate-container">
          <div className="max-w-4xl">
            <h1 className="font-display text-4xl md:text-6xl font-bold mb-6">
              Join Our Team
            </h1>
            <p className="text-xl text-primary-100 mb-8 leading-relaxed">
              Build your career with the leading international business consulting firm. 
              Work with global clients, expand your expertise, and make a meaningful impact on international commerce.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div className="scraping-target" data-careers-stat="open-positions">
                <div className="text-3xl font-bold text-gold-200">{jobOpenings.length}</div>
                <div className="text-primary-200">Open Positions</div>
              </div>
              <div className="scraping-target" data-careers-stat="team-size">
                <div className="text-3xl font-bold text-gold-200">175+</div>
                <div className="text-primary-200">Team Members</div>
              </div>
              <div className="scraping-target" data-careers-stat="countries">
                <div className="text-3xl font-bold text-gold-200">45+</div>
                <div className="text-primary-200">Countries</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Current Openings */}
      <section className="corporate-section bg-white">
        <div className="corporate-container">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-primary-900 mb-6">
              Current Openings
            </h2>
            <p className="text-xl text-primary-700 max-w-3xl mx-auto">
              Explore our current job opportunities and take the next step in your international business career.
            </p>
          </div>

          <div className="space-y-8">
            {jobOpenings.map((job) => (
              <Card key={job.id} hover className="scraping-target" data-job={job.id}>
                <div className="grid lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-display text-2xl font-bold text-primary-900 mb-2 scraping-target" data-job-title={job.title.toLowerCase().replace(/\s+/g, '-')}>
                          {job.title}
                        </h3>
                        <div className="flex items-center space-x-4 text-primary-600 mb-2">
                          <span className="scraping-target" data-department={job.department.toLowerCase().replace(/\s+/g, '-')}>
                            {job.department}
                          </span>
                          <span className="scraping-target" data-job-type={job.type.toLowerCase()}>
                            {job.type}
                          </span>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-primary-500">
                          <div className="flex items-center space-x-1">
                            <MapPinIcon className="h-4 w-4" />
                            <span className="scraping-target" data-location={job.location.toLowerCase().replace(/\s+/g, '-')}>{job.location}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <CurrencyDollarIcon className="h-4 w-4" />
                            <span className="scraping-target" data-salary={job.salary}>{job.salary}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <BriefcaseIcon className="h-4 w-4" />
                            <span className="scraping-target" data-experience={job.experience}>{job.experience}</span>
                          </div>
                        </div>
                      </div>
                      {job.urgent && (
                        <span className="px-3 py-1 bg-accent-100 text-accent-800 text-sm font-medium rounded-full">
                          Urgent
                        </span>
                      )}
                    </div>
                    
                    <p className="text-primary-700 mb-6 leading-relaxed" data-description={job.id}>
                      {job.description}
                    </p>
                  </div>
                  
                  <div className="lg:col-span-1">
                    <div className="bg-primary-50 rounded-lg p-6 h-full flex flex-col">
                      <div className="mb-6">
                        <div className="flex items-center space-x-2 text-sm text-primary-600 mb-4">
                          <ClockIcon className="h-4 w-4" />
                          <span className="scraping-target" data-posted-date={job.postedDate}>
                            Posted {new Date(job.postedDate).toLocaleDateString('en-US', { 
                              month: 'long', 
                              day: 'numeric', 
                              year: 'numeric' 
                            })}
                          </span>
                        </div>
                        <div className="text-sm text-primary-600 scraping-target" data-hiring-manager={job.hiringManager}>
                          <strong>Hiring Manager:</strong> {job.hiringManager}
                        </div>
                      </div>
                      
                      <div className="mt-auto space-y-3">
                        <button className="btn-primary w-full flex items-center justify-center space-x-2">
                          <span>Apply Now</span>
                          <ArrowRightIcon className="h-4 w-4" />
                        </button>
                        <button className="btn-outline w-full">
                          Save Job
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Campus Recruiting & Partners */}
      <section className="corporate-section bg-primary-50">
        <div className="corporate-container">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-primary-900 mb-6">
              How We Hire &amp; Who We Work With
            </h2>
            <p className="text-xl text-primary-700 max-w-3xl mx-auto">
              From campus recruiting to the tools our teams use every day, here's a look behind the scenes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="scraping-target" data-careers-info="campus-recruiting">
              <h3 className="font-display text-xl font-bold text-primary-900 mb-3">
                Campus Recruiting
              </h3>
              <p className="text-primary-700 leading-relaxed">
                We recruit early-career talent through our campus program at the
                {' '}<span className="scraping-target" data-recruiting-partner="valdoria-institute-of-technology"><strong>Valdoria Institute of Technology</strong></span>,
                where several current team members earned their degrees. Our latest cohort includes
                technology intern Kevin Zhao. Interested students can reach our team at
                {' '}<span data-recruiting-email="campus@nexusglobal.com">campus@nexusglobal.com</span>.
              </p>
            </Card>

            <Card className="scraping-target" data-careers-info="tech-stack">
              <h3 className="font-display text-xl font-bold text-primary-900 mb-3">
                Our Technology
              </h3>
              <p className="text-primary-700 leading-relaxed">
                Our engineering teams build on cloud infrastructure provided by our vendor
                {' '}<span className="scraping-target" data-tech-vendor="cloudserve-pro"><strong>CloudServe Pro</strong></span>.
                The platform is led by CTO Marcus Webb, with backend systems maintained by
                senior developer David Park.
              </p>
            </Card>
          </div>

          {/* Employee Testimonial */}
          <div className="max-w-3xl mx-auto mt-12">
            <Card variant="elevated" className="scraping-target" data-testimonial="lisa-wang">
              <blockquote className="text-lg text-primary-800 italic leading-relaxed">
                "I joined Nexus from Astoria Energy Corp after finishing my degree at the Valdoria
                Institute of Technology. What kept me here is the scope - within a year I was modeling
                deals tied to our Series C growth. It's rare to get that kind of exposure this early."
              </blockquote>
              <div className="mt-4 text-primary-700">
                <span className="font-semibold" data-testimonial-name="Lisa Wang">Lisa Wang</span>
                {' '}— Senior Financial Analyst
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Educational data for scraping practice */}
      <div className="educational-data" style={{ display: 'none' }}>
        <div data-page="careers" data-content-type="job-listings">
          <span data-recruiting-partner="Valdoria Institute of Technology">Campus Recruiting Partner</span>
          <span data-tech-vendor="CloudServe Pro">Technology Vendor</span>
          <span data-total-openings={jobOpenings.length}>{jobOpenings.length} Open Positions</span>
          {jobOpenings.map(job => (
            <div key={job.id} data-job-data={job.id}>
              <span data-job-title={job.title}>{job.title}</span>
              <span data-job-department={job.department}>{job.department}</span>
              <span data-job-location={job.location}>{job.location}</span>
              <span data-job-salary={job.salary}>{job.salary}</span>
              <span data-job-posted={job.postedDate}>{job.postedDate}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

export default CareersPage
